import { useState, useEffect, useRef } from "react";
import { StepPreCadastro } from "@/components/simulator/StepPreCadastro";
import { StepLeiloeiro } from "@/components/simulator/StepLeiloeiro";
import { StepFotos } from "@/components/simulator/StepFotos";
import { StepDadosVeiculo } from "@/components/simulator/StepDadosVeiculo";
import { StepResultado } from "@/components/simulator/StepResultado";
import { StepPlano } from "@/components/simulator/StepPlano";
import { SimulatorStepper } from "@/components/simulator/SimulatorStepper";
import { Badge } from "@/components/ui/badge";
import type { SimulatorData, AnalysisResult, UserLocation } from "@/lib/simulator";
import { saveSimulation } from "@/lib/simulator";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

// New Order:
// 0: Leiloeiro
// 1: Fotos
// 2: DadosVeiculo
// 3: PreCadastro (Capture)
// 4: Resultado
// 5: Plano

const STEP_TITLES = [
  "Qual a origem mecânica do veículo?",
  "O que os seus olhos não veem, a IA detecta.",
  "Precisão cirúrgica. Dados reais.",
  "Laudo pronto. Para onde enviamos?",
  "A verdade sobre este lote.",
  "Desbloqueie o poder analítico."
];

const STEP_SUBTITLES = [
  "Selecionar o leiloeiro correto permite cruzamento de dados com editais ocultos.",
  "Uma foto da avaria principal pode mudar a precificação em até 40%.",
  "Nossa IA só precisa de Marca, Modelo e Ano para puxar o histórico completo.",
  "Não fazemos spam. Apenas análise bruta.",
  "Os números não mentem. O leilão sim.",
  "Veja componentes, falhas ocultas e mercado real."
];

export default function Simulador() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<Partial<SimulatorData>>({});
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const isLoggedIn = useRef(false);
  const [isPro, setIsPro] = useState(false);
  const [quotaBlocked, setQuotaBlocked] = useState<{
    open: boolean;
    limit: number;
    count: number;
  }>({ open: false, limit: 0, count: 0 });
  const [checkingQuota, setCheckingQuota] = useState(false);
  const navigate = useNavigate();

  // Read location from localStorage (set in Header)
  const [userLocation] = useLocalStorage<UserLocation>("user_location", { cidade: "", estado: "" });

  // Check auth + pro status
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        isLoggedIn.current = true;
        setData((prev) => ({ ...prev, email: session.user.email || "" }));
        // If logged in, we can skip step 3 entirely later, but for now we keep the base flow

        // Check if user is pro
        const { data: profile } = await supabase
          .from("profile")
          .select("plan, payment_status")
          .eq("user_id", session.user.id)
          .single();

        if (profile?.plan === "pro" && profile?.payment_status === "active") {
          setIsPro(true);
        }
      }
    });
  }, []);

  const update = (partial: Partial<SimulatorData>) =>
    setData((prev) => ({ ...prev, ...partial }));

  // Check & consume quota before allowing analysis (step 4)
  async function consumeQuotaAndAdvance(toStep: number, emailOverride?: string) {
    if (isPro) {
      setStep(toStep);
      return;
    }
    setCheckingQuota(true);
    try {
      const email = emailOverride || data.email;
      const { data: res, error } = await supabase.functions.invoke(
        "check-simulation-quota",
        { body: { email } }
      );
      if (error) throw error;
      if (res?.allowed) {
        setStep(toStep);
      } else {
        setQuotaBlocked({
          open: true,
          limit: res?.limit ?? 0,
          count: res?.count ?? 0,
        });
      }
    } catch (e: any) {
      console.error("quota check failed:", e);
      // Fail open to avoid breaking UX, but warn
      toast.error("Não foi possível verificar o limite de simulações. Continuando...");
      setStep(toStep);
    } finally {
      setCheckingQuota(false);
    }
  }

  const DigitalGlitch = ({ trigger }: { trigger: any }) => {
    const [isGlitching, setIsGlitching] = useState(false);
    useEffect(() => {
      setIsGlitching(true);
      const timer = setTimeout(() => setIsGlitching(false), 200);
      return () => clearTimeout(timer);
    }, [trigger]);

    if (!isGlitching) return null;

    return (
      <div className="fixed inset-0 z-[100] pointer-events-none mix-blend-overlay opacity-30">
        <div className="absolute inset-0 bg-[#000] animate-pulse" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] bg-repeat" />
      </div>
    );
  };

  // Activity Ticker Component
  const ActivityTicker = () => {
    const [index, setIndex] = useState(0);
    const activities = [
      `Análise iniciada em ${userLocation.cidade || "São Paulo"}/${userLocation.estado || "SP"}...`,
      "Toyota Corolla avaliado com Score 92...",
      "Nova consulta de histórico FIPE detectada...",
      "Lote 452 analisado: Margem de 15% identificada.",
      "Aguardando fotos do compartimento do motor...",
    ];

    useEffect(() => {
      const timer = setInterval(() => {
        setIndex((prev) => (prev + 1) % activities.length);
      }, 4000);
      return () => clearInterval(timer);
    }, []);

    return (
      <div className="h-6 overflow-hidden text-xs uppercase tracking-[0.2em] font-black text-foreground/40 mt-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 bg-primary animate-pulse rounded-full" />
            {activities[index]}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  };

  async function handlePlanSelected(plan: "starter" | "pro" | "enterprise") {
    if (!analysisResult) return;
    const simId = await saveSimulation(data, analysisResult, userLocation);
    if (simId) {
      toast.success("Simulação salva com sucesso!");
    } else {
      toast.error("Erro ao salvar simulação. Faça login e tente novamente.");
    }
  }

  // Handle advancing from DadosVeiculo
  const handleDadosVeiculoNext = (vehicleData: any) => {
    update(vehicleData);
    if (isLoggedIn.current) {
      // Skip PreCadastro if already logged in — check quota first
      consumeQuotaAndAdvance(4);
    } else {
      setStep(3); // Go to PreCadastro
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background flex flex-col md:flex-row relative">
      <DigitalGlitch trigger={step} />

      {/* Left Column: Fixed / Sticky Typography Narrative */}
      <div className="w-full md:w-2/5 lg:w-1/3 p-8 lg:p-16 flex flex-col justify-between border-b md:border-b-0 md:border-r border-border/50 bg-muted/10 relative overflow-hidden">
        {/* Decorative Grid or Glitch Element */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none opacity-50"></div>

        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="space-y-4"
            >
              <Badge variant="outline" className="font-black uppercase tracking-widest border-primary/50 text-primary">
                ETAPA {step + 1} de {STEP_TITLES.length}
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-heading font-black uppercase tracking-tighter leading-none">
                {STEP_TITLES[step].split(". ").map((part, i) => (
                  <span key={i} className="block">
                    {part}{i === 0 && STEP_TITLES[step].includes(". ") ? "." : ""}
                  </span>
                ))}
              </h1>
              <p className="text-muted-foreground text-lg font-medium leading-tight max-w-sm">
                {STEP_SUBTITLES[step]}
              </p>

              <ActivityTicker />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 relative z-10 hidden md:block">
          <SimulatorStepper currentStep={step} />
        </div>
      </div>

      {/* Right Column: Dynamic Form Area (No Cards) */}
      <div className="w-full md:w-3/5 lg:w-2/3 p-6 md:p-12 lg:p-20 flex items-center justify-center relative overflow-y-auto">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20, filter: "blur(4px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: -20, filter: "blur(4px)" }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* Step 0: Leiloeiro */}
              {step === 0 && (
                <StepLeiloeiro
                  onNext={(leiloeiro) => {
                    update({ leiloeiro });
                    setStep(1);
                  }}
                  onBack={() => { /* First step, no back */ }}
                />
              )}

              {/* Step 1: Fotos */}
              {step === 1 && (
                <StepFotos
                  leadId={data.leadId || "anonymous"}
                  onNext={(fotos, fotoUrls) => {
                    update({ fotos, fotoUrls });
                    setStep(2);
                  }}
                  onBack={() => setStep(0)}
                />
              )}

              {/* Step 2: DadosVeiculo */}
              {step === 2 && (
                <StepDadosVeiculo
                  leiloeiro={data.leiloeiro || ""}
                  onNext={handleDadosVeiculoNext}
                  onBack={() => setStep(1)}
                />
              )}

              {/* Step 3: PreCadastro (Only if not logged in) */}
              {step === 3 && (
                <StepPreCadastro
                  onNext={(email, leadId) => {
                    update({ email, leadId });
                    consumeQuotaAndAdvance(4, email);
                  }}
                  onBack={() => setStep(2)}
                />
              )}

              {/* Step 4: Resultado */}
              {step === 4 && (
                <StepResultado
                  valorFipe={data.valorFipe || 0}
                  valorFipeFormatado={data.valorFipeFormatado || ""}
                  marcaNome={data.marcaNome || ""}
                  modeloNome={data.modeloNome || ""}
                  anoNome={data.anoNome || ""}
                  km={data.km || 0}
                  leiloeiro={data.leiloeiro || ""}
                  observacoes={data.observacoes || ""}
                  fotoUrls={data.fotoUrls || []}
                  localizacao={userLocation}
                  isPro={isPro}
                  simulatorData={data}
                  onNextStep={() => setStep(5)}
                  onAnalysisComplete={(r) => setAnalysisResult(r)}
                  onBack={() => setStep(isLoggedIn.current ? 2 : 3)}
                />
              )}

              {/* Step 5: Plano */}
              {step === 5 && (
                <StepPlano
                  onSelectPlan={handlePlanSelected}
                  onBack={() => setStep(4)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div >

      <Dialog
        open={quotaBlocked.open}
        onOpenChange={(o) => setQuotaBlocked((p) => ({ ...p, open: o }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Limite de simulações grátis atingido
            </DialogTitle>
            <DialogDescription>
              Você já utilizou {quotaBlocked.count} de {quotaBlocked.limit} simulações
              gratuitas neste mês. Faça upgrade para o plano <strong>PRO</strong> e tenha
              análises ilimitadas, relatórios em PDF e dados de mercado completos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setQuotaBlocked((p) => ({ ...p, open: false }))}
            >
              Fechar
            </Button>
            <Button onClick={() => navigate("/precos")}>Conhecer plano PRO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkingQuota && (
        <div className="fixed inset-0 z-[90] bg-background/60 backdrop-blur-sm flex items-center justify-center">
          <div className="text-sm font-bold uppercase tracking-widest text-primary animate-pulse">
            Verificando acesso...
          </div>
        </div>
      )}
    </div >
  );
}
